import type { ContentLang } from '@yoyoclub/shared';

/**
 * Pass API Service
 * Handles all pass-related API calls
 */

export interface PassDetail {
  id: string;
  slug: string;
  titleCN: string | null;
  titleEN: string | null;
  descriptionCN?: string | null;
  descriptionEN?: string | null;
  price: number; // cents
  totalSessions: number;
  expiresAt: string;
  maxSales: number;
  soldCount: number;
  status: 'ACTIVE' | 'PAUSED' | 'PENDING_APPROVAL';
  isBuyable: boolean;
  activity: {
    id: string;
    titleCN: string | null;
    titleEN: string | null;
    slug: string;
    descriptionCN?: string | null;
    descriptionEN?: string | null;
    category?: string;
    ageMin?: number;
    ageMax?: number;
    duration?: number;
    location?: string;
    locationDetails?: string;
    region?: string[];
    childRequired?: boolean;
    images: Array<{ imageUrl: string; isCover: boolean; order: number }>;
  };
  host?: {
    id: string;
    displayName: string;
  };
  sessions: Array<{
    session: {
      id: string;
      startTime: string;
      endTime: string;
      maxCapacity: number;
      currentBookings: number;
    };
  }>;
}

export interface UserPassDetail {
  id: string;
  passId: string;
  remainingSessions: number;
  usedSessions: number;
  expiresAt: string;
  status: 'ACTIVE' | 'REFUNDED';
  paymentAmount: number; // cents
  storeCreditUsed?: number | null;
  purchasedAt: string;
  displayStatus: 'active' | 'used_up' | 'expired' | 'refunded';
  pass: {
    id: string;
    slug: string;
    titleCN: string | null;
    titleEN: string | null;
    totalSessions: number;
    price: number;
    activity: {
      id: string;
      titleCN: string | null;
      titleEN: string | null;
      slug: string;
      images: Array<{ imageUrl: string; isCover: boolean }>;
    };
    sessions?: Array<{
      session: {
        id: string;
        startTime: string;
        endTime: string;
        maxCapacity: number;
        currentBookings: number;
      };
    }>;
  };
  bookings?: Array<{
    id: string;
    status: string;
    createdAt: string;
    session: {
      id: string;
      startTime: string;
      endTime: string;
    };
  }>;
}

export interface AvailablePassForSession {
  id: string; // userPassId
  remainingSessions: number;
  usedSessions: number;
  expiresAt: string;
  pass: {
    id: string;
    titleCN: string | null;
    titleEN: string | null;
    totalSessions: number;
  };
}

export interface PurchasePassResult {
  checkoutUrl: string | null;
  checkoutSessionId?: string;
  userPassId?: string;
  creditApplied?: number;
}

export interface RefundPassResult {
  refundedAmount: number; // cents
  userPassId: string;
}

// Admin pass buyer detail
export interface PassBuyerDetail {
  id: string; // userPassId
  user: {
    id: string;
    name: string | null;
    email?: string;
  };
  remainingSessions: number;
  usedSessions: number;
  expiresAt: string;
  status: 'ACTIVE' | 'REFUNDED';
  displayStatus: 'active' | 'used_up' | 'expired' | 'refunded';
  paymentAmount: number;
  storeCreditUsed: number | null;
  purchasedAt: string;
  bookings: Array<{
    id: string;
    status: string;
    session: {
      id: string;
      startTime: string;
      endTime: string;
    };
    createdAt: string;
  }>;
}

// Admin/Host management interfaces
export interface AdminPassListItem {
  id: string;
  slug: string;
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang?: ContentLang | null;
  descriptionCN?: string | null;
  descriptionEN?: string | null;
  descriptionAutoLang?: ContentLang | null;
  price: number; // cents
  totalSessions: number;
  expiresAt: string;
  maxSales: number;
  soldCount: number;
  status: 'ACTIVE' | 'PAUSED' | 'PENDING_APPROVAL';
  createdAt: string;
  activity: {
    id: string;
    titleCN: string | null;
    titleEN: string | null;
    slug: string;
  };
  host?: {
    id: string;
    displayName: string;
  };
  sessions: Array<{
    session: { id: string; startTime: string; endTime: string };
  }>;
  _count: { userPasses: number };
}

export interface CreatePassData {
  activityId: string;
  hostId: string;
  titleCN?: string | null;
  titleEN?: string | null;
  titleAutoLang?: ContentLang | null;
  descriptionCN?: string | null;
  descriptionEN?: string | null;
  descriptionAutoLang?: ContentLang | null;
  price: number; // cents
  totalSessions: number;
  expiresAt: string; // ISO date
  maxSales: number;
  sessionIds: string[];
}

export interface UpdatePassData {
  titleCN?: string | null;
  titleEN?: string | null;
  titleAutoLang?: ContentLang | null;
  descriptionCN?: string | null;
  descriptionEN?: string | null;
  descriptionAutoLang?: ContentLang | null;
  price?: number;
  totalSessions?: number;
  maxSales?: number;
  expiresAt?: string;
}

class PassApi {
  private apiUrl: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
  }

  private getErrorMessageFromPayload(payload: unknown): string | null {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (!payload || typeof payload !== 'object') return null;

    const body = payload as { error?: unknown; message?: unknown };
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
    if (typeof body.message === 'string' && body.message.trim()) return body.message;

    if (body.error && typeof body.error === 'object') {
      const nestedError = body.error as { message?: unknown };
      if (typeof nestedError.message === 'string' && nestedError.message.trim()) {
        return nestedError.message;
      }
    }

    return null;
  }

  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    const clonedResponse = response.clone();

    try {
      const payload: unknown = await response.json();
      const message = this.getErrorMessageFromPayload(payload);
      if (message) return message;
    } catch {
      // Non-JSON error response; fall back to plain text.
    }

    try {
      const text = (await clonedResponse.text()).trim();
      if (text) return text;
    } catch {
      // Ignore and return fallback.
    }

    return fallback;
  }

  /**
   * Get Pass detail by slug (public, no auth)
   */
  async getPassBySlug(slug: string): Promise<PassDetail> {
    const response = await fetch(`${this.apiUrl}/passes/slug/${slug}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to fetch pass'));
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get Pass detail by id (public, used by checkout page refresh fallback)
   */
  async getPassById(id: string): Promise<PassDetail> {
    const response = await fetch(`${this.apiUrl}/passes/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to fetch pass'));
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get all buyable passes for an activity (public, no auth)
   */
  async getPassesForActivity(activitySlug: string): Promise<PassDetail[]> {
    const response = await fetch(`${this.apiUrl}/activities/slug/${activitySlug}/passes`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to fetch passes'));
    }

    const result = await response.json();
    return result.data || [];
  }

  /**
   * Purchase a pass (requires auth)
   */
  /** Check a member's new user promo code for this pass; a pass takes no platform promo code. */
  async validateInvitationCode(
    passId: string,
    code: string,
    token: string
  ): Promise<{
    valid: boolean;
    discountType?: 'FIXED_AMOUNT' | 'PERCENTAGE';
    discountValue?: number;
    reason?: string;
    message: string;
    isMemberCode?: boolean;
  }> {
    const response = await fetch(`${this.apiUrl}/passes/${passId}/validate-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to check the code'));
    }
    return (await response.json()).data;
  }

  async purchasePass(
    passId: string,
    policyAcceptedAt: string,
    token: string,
    locale?: string, // Site language; sets the Stripe page language and copy
    invitationCode?: string // A member's new user promo code (member rewards PRD §3.2)
  ): Promise<PurchasePassResult> {
    const response = await fetch(`${this.apiUrl}/passes/${passId}/purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        policyAcceptedAt,
        locale,
        ...(invitationCode ? { promotionCode: invitationCode } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to purchase pass'));
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get user's passes (requires auth)
   */
  async getUserPasses(token: string): Promise<UserPassDetail[]> {
    const response = await fetch(`${this.apiUrl}/passes/user`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to fetch passes'));
    }

    const result = await response.json();
    return result.data || [];
  }

  /**
   * Get single user pass detail (requires auth)
   */
  async getUserPassById(userPassId: string, token: string): Promise<UserPassDetail> {
    const response = await fetch(`${this.apiUrl}/passes/user/${userPassId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to fetch pass'));
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Refund an unused, unexpired pass (requires auth)
   */
  async refundUserPass(userPassId: string, token: string): Promise<RefundPassResult> {
    const response = await fetch(`${this.apiUrl}/passes/user/${userPassId}/refund`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to refund pass'));
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get available passes for a session (requires auth)
   */
  async getAvailablePassesForSession(
    sessionId: string,
    token: string
  ): Promise<AvailablePassForSession[]> {
    const response = await fetch(`${this.apiUrl}/sessions/${sessionId}/available-passes`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to check available passes'));
    }

    const result = await response.json();
    return result.data || [];
  }

  // ─── Admin/Host Management Methods ──────────────────────────────

  /**
   * Get all buyers for a pass (admin only)
   */
  async getPassBuyers(passId: string, token: string): Promise<PassBuyerDetail[]> {
    const response = await fetch(`${this.apiUrl}/passes/${passId}/buyers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to fetch pass buyers'));
    }

    const result = await response.json();
    return result.data || [];
  }

  /**
   * Get all passes (admin gets all, host gets own)
   */
  async getAdminPasses(token: string): Promise<AdminPassListItem[]> {
    const response = await fetch(`${this.apiUrl}/passes/host`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to fetch passes'));
    }

    const result = await response.json();
    return result.data || [];
  }

  /**
   * Create a new pass (admin/host)
   */
  async createPass(data: CreatePassData, token: string): Promise<AdminPassListItem> {
    const response = await fetch(`${this.apiUrl}/passes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to create pass'));
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Update a pass (admin/host)
   */
  async updatePass(id: string, data: UpdatePassData, token: string): Promise<AdminPassListItem> {
    const response = await fetch(`${this.apiUrl}/passes/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update pass'));
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Toggle pass status (ACTIVE/PAUSED)
   */
  async updatePassStatus(id: string, status: 'ACTIVE' | 'PAUSED', token: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/passes/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update pass status'));
    }
  }

  /**
   * Delete a pass (admin/host, only if soldCount === 0)
   */
  async deletePass(id: string, token: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/passes/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to delete pass'));
    }
  }

  /**
   * Add sessions to a pass
   */
  async addPassSessions(id: string, sessionIds: string[], token: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/passes/${id}/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ sessionIds }),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to add sessions'));
    }
  }

  /**
   * Remove a session from a pass
   */
  async removePassSession(id: string, sessionId: string, token: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/passes/${id}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to remove session'));
    }
  }

  // ─── User Methods ─────────────────────────────────────────────

  /**
   * Book a session using a pass (requires auth)
   */
  async bookWithPass(
    sessionId: string,
    userPassId: string,
    participants: {
      kids: Array<{
        profileId: string;
        name: string;
        birthDate: string;
        notes?: string;
      }>;
      adults: Array<{
        profileId: string;
        name: string;
        phone: string;
      }>;
    },
    consent: {
      photoVideoConsent: 'FULL' | 'BLURRED' | 'NO_PHOTO';
      waiverAgreed: boolean;
      releaseAgreed: boolean;
    },
    token: string,
    transferCode?: string | null
  ): Promise<{ id: string }> {
    const response = await fetch(`${this.apiUrl}/sessions/${sessionId}/book-with-pass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userPassId,
        participants,
        ...consent,
        ...(transferCode ? { transferCode } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to book with pass'));
    }

    const result = await response.json();
    return result.data;
  }
}

export const passApi = new PassApi();
